import React, { useState, useEffect, useCallback } from 'react';
import './SummaryReportForAdmin.scss';
import { useDispatch } from 'react-redux';
import { Redirect } from 'react-router-dom';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter, faFileExcel } from '@fortawesome/free-solid-svg-icons';
import {
  getFilterDropDownData,
  getIndentSummaryReport,
  getIndentSummaryReportCsv,
} from '../../../store/modules/product/actions';

const HNL_REPORT_EMAILS = [
  'l1@heritagenutrivet.in',
  'hnllogistics@heritagenutrivet.in',
  'shantanu.deshkar@heritagenutrivet.in',
  'l7@heritagenutrivet.in',
  'l5@heritagenutrivet.in',
  'l3@heritagenutrivet.in',
];
const CUSTOM_DATE_MODE = 'CUSTOM';

const REPORT_TYPES = [
  {
    key: 'orderVsDispatched',
    label: 'Order Quantity vs Dispatched',
    shortLabel: 'Order vs Dispatched',
    metricLabel: 'Fulfilled Qty',
  },
  {
    key: 'orderVsApproved',
    label: 'Order Quantity vs Approval Qty',
    shortLabel: 'Order vs Approval',
    metricLabel: 'Approved Qty',
  },
  {
    key: 'orderedVsRejected',
    label: 'Order Quantity vs Rejected Qty',
    shortLabel: 'Order vs Rejected',
    metricLabel: 'Rejected Qty',
  },
  {
    key: 'approvedVsDispatched',
    label: 'Approval Qty vs Dispatched Qty',
    shortLabel: 'Approval vs Dispatched',
    metricLabel: 'Dispatched Qty',
  },
  {
    key: 'dispatchVsReceived',
    label: 'Dispatch vs Received',
    shortLabel: 'Dispatch vs Received',
    metricLabel: 'Received Qty',
  },
];

const INSIGHT_CARDS = [
  { key: 'totalOrderedQty', label: 'Total Ordered Qty' },
  { key: 'totalApprovedQty', label: 'Total Approved Qty' },
  { key: 'totalPendingApprovalQty', label: 'Pending Approval Qty' },
  { key: 'totalRejectedQty', label: 'Rejected Qty' },
  { key: 'totalDispatchedQty', label: 'Total Dispatched Qty' },
  { key: 'totalPendingDispatchQty', label: 'Pending Dispatch Qty' },
  { key: 'totalReceivedQty', label: 'Total Received Qty' },
  { key: 'totalPendingReceivedQty', label: 'Pending Received Qty' },
];

const DATE_MODES = [
  { value: 'MTD', label: 'MTD' },
  { value: 'WTD', label: 'WTD' },
  { value: 'QTD', label: 'QTD' },
  { value: 'YTD', label: 'YTD' },
  { value: CUSTOM_DATE_MODE, label: 'Custom date range' },
];

const CATEGORY_TABLES = [
  {
    key: 'feed',
    prefix: 'feed',
    title: 'Feed (In Tons)',
    unit: 'tons',
    totalQtyKey: 'totalFeedQty',
    totalFullFillKey: 'totalFeedFullFillQty',
    totalPercentKey: 'totalFeedFullFillPer',
  },
  {
    key: 'supplements',
    prefix: 'supp',
    title: 'Feed Supplement (In Kgs)',
    unit: 'kgs',
    totalQtyKey: 'totalSuppQty',
    totalFullFillKey: 'totalSuppFullFillQty',
    totalPercentKey: 'totalSuppFullFillPer',
  },
  {
    key: 'pharmacy',
    prefix: 'pharmacy',
    title: 'Pharmacy (In Kgs)',
    unit: 'kgs',
    totalQtyKey: 'totalPharmacyQty',
    totalFullFillKey: 'totalPharmacyFullFillQty',
    totalPercentKey: 'totalPharmacyFullFillPer',
  },
];

const TABLE_LABELS = {
  orderVsDispatched: {
    primary: 'Order Quantity',
    secondary: 'Dispatched Quantity',
    percent: 'Dispatched %',
  },
  orderVsApproved: {
    primary: 'Order Quantity',
    secondary: 'Approved Quantity',
    percent: 'Approved %',
  },
  orderedVsRejected: {
    primary: 'Order Quantity',
    secondary: 'Rejected Quantity',
    percent: 'Rejected %',
  },
  approvedVsDispatched: {
    primary: 'Approved Quantity',
    secondary: 'Dispatched Quantity',
    percent: 'Dispatched %',
  },
  dispatchVsReceived: {
    primary: 'Dispatched Quantity',
    secondary: 'Received Quantity',
    percent: 'Received %',
  },
};

const getRows = (rows) => (Array.isArray(rows) ? rows : []);

const formatNumber = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return 0;
  return Math.round(number * 100) / 100;
};

const formatInputDate = (value) => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const getRowValue = (row, keys) => {
  const match = keys.find(
    (key) => row?.[key] !== undefined && row?.[key] !== null && row?.[key] !== '',
  );
  return match ? row[match] : '';
};

const getCategoryKey = (category = '') => {
  const value = String(category).toLowerCase();
  if (value.includes('pharmacy')) return 'pharmacy';
  if (value.includes('supplement')) return 'supplements';
  return 'feed';
};

const normaliseSummaryRow = (row, fallback = {}) => ({
  regionName: getRowValue(row, ['regionName']) || fallback.regionName || 'Unknown Region',
  plantName: getRowValue(row, ['plantName', 'plantCode']) || fallback.plantName || 'Unknown Plant',
  materialName:
    getRowValue(row, ['materialName', 'productName', 'materialDescription'])
    || fallback.materialName
    || 'Total',
  productCategory:
    getRowValue(row, ['productCategory', 'feedType', 'category'])
    || fallback.productCategory
    || '',
  orderedQty: Number(getRowValue(row, ['orderedQty', 'orderQuantity', 'indentQuantity'])) || 0,
  fullFilledQty:
    Number(getRowValue(row, ['fullFilledQty', 'fulfilledQty', 'dispatchQuantity', 'receivedQuantity', 'pendingQuantity'])) || 0,
  fulfillmentPercentage:
    Number(getRowValue(row, ['fulfillmentPercentage', 'percentage', 'dispatchPercentage', 'receivedPercentage'])) || 0,
});

const getResponseRows = (response) => {
  if (Array.isArray(response)) return response.map((row) => normaliseSummaryRow(row));
  if (Array.isArray(response?.rows)) return response.rows.map((row) => normaliseSummaryRow(row));
  if (Array.isArray(response?.data)) return response.data.map((row) => normaliseSummaryRow(row));
  if (Array.isArray(response?.paginate)) {
    return response.paginate.map((row) => normaliseSummaryRow(row));
  }
  return [
    ...getRows(response?.feed).map((row) => normaliseSummaryRow(row, { productCategory: 'Feed' })),
    ...getRows(response?.supplements).map((row) => normaliseSummaryRow(row, { productCategory: 'Feed Supplement' })),
    ...getRows(response?.pharmacy).map((row) => normaliseSummaryRow(row, { productCategory: 'Pharmacy' })),
  ];
};

const getCardTotal = (card) => {
  if (card && typeof card === 'object') {
    return getRowValue(card, ['totalQuantity', 'quantity', 'value', 'total', 'count']);
  }
  if (card !== undefined && card !== null) return card;
  return 0;
};

const normaliseInsightCards = (response) => {
  const totals = response?.summaryTotals || response?.cards || {};
  return INSIGHT_CARDS.reduce((acc, card) => {
    acc[card.key] = formatNumber(getCardTotal(totals?.[card.key]));
    return acc;
  }, {});
};

const buildQueryValues = ({
  reportType,
  regionName,
  plantName,
  productCategory,
  productName,
  dateMode,
  startDate,
  endDate,
}) => ({
  reportType,
  regionName,
  plantName,
  productCategory,
  productName,
  ...(dateMode === CUSTOM_DATE_MODE ? { startDate, endDate } : { datePreset: dateMode }),
});

const getSelectOptions = (items, selectedValue) => getRows(items)
  .map(
    (item) => `<option value="${escapeHtml(item)}" ${
      item === selectedValue ? 'selected' : ''
    }>${escapeHtml(item)}</option>`,
  )
  .join('');

const getDisplayQuantity = (value, category) => (
  category.unit === 'kgs' ? (Number(value) || 0) * 1000 : Number(value) || 0
);

const formatCategoryQuantity = (value, category) => (
  formatNumber(getDisplayQuantity(value, category))
);

const buildCategorySummary = (categoryRows) => {
  const orderedQty = categoryRows.reduce((total, row) => total + row.orderedQty, 0);
  const fullFilledQty = categoryRows.reduce((total, row) => total + row.fullFilledQty, 0);
  return {
    orderedQty,
    fullFilledQty,
    fulfillmentPercentage: orderedQty ? formatNumber((fullFilledQty / orderedQty) * 100) : 0,
  };
};

// Groups a flat list of region+plant+material+category rows by regionName or
// plantName, rolling each group up into feed/supplements/pharmacy totals -
// used to derive region and plant tier summaries when the backend doesn't
// supply them pre-aggregated for that grouping.
const buildGroupedCategoryRows = (rowsList, groupKey) => {
  const map = new Map();
  rowsList.forEach((row) => {
    const key = row[groupKey];
    if (!map.has(key)) {
      map.set(key, { [groupKey]: key, byCategory: { feed: [], supplements: [], pharmacy: [] } });
    }
    map.get(key).byCategory[getCategoryKey(row.productCategory)].push(row);
  });
  return Array.from(map.values()).map((entry) => {
    const summary = { [groupKey]: entry[groupKey] };
    CATEGORY_TABLES.forEach((category) => {
      const totals = buildCategorySummary(entry.byCategory[category.key]);
      summary[`${category.prefix}Qty`] = totals.orderedQty;
      summary[`${category.prefix}FullFillQty`] = totals.fullFilledQty;
      summary[`${category.prefix}FullFillPer`] = totals.fulfillmentPercentage;
    });
    return summary;
  });
};

const getPlantRows = (response, rows) => (
  Array.isArray(response?.plantRows) && response.plantRows.length
    ? response.plantRows.map((row) => normaliseSummaryRow(row))
    : rows
);

const getRegionRows = (response, plantRowsAll) => (
  Array.isArray(response?.regionRows) && response.regionRows.length
    ? response.regionRows
    : buildGroupedCategoryRows(plantRowsAll, 'regionName')
);

const getPlantSummaryRows = (plantRowsForRegion) => (
  buildGroupedCategoryRows(plantRowsForRegion, 'plantName')
);

const getGrandTotal = (response, regionRows) => {
  const total = {};
  CATEGORY_TABLES.forEach((category) => {
    if (
      response?.[category.totalQtyKey] !== undefined
      || response?.[category.totalFullFillKey] !== undefined
      || response?.[category.totalPercentKey] !== undefined
    ) {
      total[category.key] = {
        orderedQty: Number(response?.[category.totalQtyKey]) || 0,
        fullFilledQty: Number(response?.[category.totalFullFillKey]) || 0,
        fulfillmentPercentage: Number(response?.[category.totalPercentKey]) || 0,
      };
      return;
    }
    const orderedQty = regionRows.reduce(
      (sum, region) => sum + (Number(region[`${category.prefix}Qty`]) || 0),
      0,
    );
    const fullFilledQty = regionRows.reduce(
      (sum, region) => sum + (Number(region[`${category.prefix}FullFillQty`]) || 0),
      0,
    );
    total[category.key] = {
      orderedQty,
      fullFilledQty,
      fulfillmentPercentage: orderedQty ? formatNumber((fullFilledQty / orderedQty) * 100) : 0,
    };
  });
  return total;
};

const getMaterialCategoryData = (materialRowsForPlant) => CATEGORY_TABLES.map((category) => {
  const categoryRows = materialRowsForPlant.filter(
    (row) => getCategoryKey(row.productCategory) === category.key,
  );
  return { ...category, rows: categoryRows, totals: buildCategorySummary(categoryRows) };
});

const MaterialDrilldownTable = ({ materialCategoryData, labels }) => {
  const maxMaterialRows = Math.max(
    ...materialCategoryData.map((category) => category.rows.length),
    0,
  );

  return (
    <table className="table table-bordered summary-report-table summary-material-table">
      <thead>
        <tr>
          {materialCategoryData.map((category) => (
            <th colSpan={4} key={category.key}>{category.title}</th>
          ))}
        </tr>
        <tr>
          {materialCategoryData.map((category) => (
            <React.Fragment key={`${category.key}-material-headers`}>
              <th>Material Wise</th>
              <th>{labels.primary}</th>
              <th>{labels.secondary}</th>
              <th>{labels.percent}</th>
            </React.Fragment>
          ))}
        </tr>
      </thead>
      <tbody>
        {maxMaterialRows > 0 ? (
          <>
            {Array.from({ length: maxMaterialRows }).map((_, rowIndex) => {
              const rowKey = materialCategoryData
                .map((category) => category.rows[rowIndex]?.materialName || 'empty')
                .join('|');

              return (
                <tr key={rowKey}>
                  {materialCategoryData.map((category) => {
                    const row = category.rows[rowIndex];
                    return (
                      <React.Fragment key={category.key}>
                        <td className="summary-material-cell">
                          {row?.materialName || ''}
                        </td>
                        <td>
                          {row ? formatCategoryQuantity(row.orderedQty, category) : ''}
                        </td>
                        <td>
                          {row ? formatCategoryQuantity(row.fullFilledQty, category) : ''}
                        </td>
                        <td>
                          {row ? formatNumber(row.fulfillmentPercentage) : ''}
                        </td>
                      </React.Fragment>
                    );
                  })}
                </tr>
              );
            })}
            <tr className="summary-total-row">
              {materialCategoryData.map((category) => (
                <React.Fragment key={`${category.key}-material-total`}>
                  <td>Total</td>
                  <td>{formatCategoryQuantity(category.totals.orderedQty, category)}</td>
                  <td>{formatCategoryQuantity(category.totals.fullFilledQty, category)}</td>
                  <td>{formatNumber(category.totals.fulfillmentPercentage)}</td>
                </React.Fragment>
              ))}
            </tr>
          </>
        ) : (
          <tr>
            <td className="summary-empty-state" colSpan={12}>
              No records found.
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
};

const HnlIndentingSummaryReport = () => {
  const dispatch = useDispatch();
  const role = localStorage.getItem('role');
  const email = (localStorage.getItem('email') || '').toLowerCase();
  const defaultRegionName = role === 'regional_pi' ? localStorage.getItem('regionName') : '';
  const canAccessReport = role === 'admin'
    || role === 'regional_pi'
    || (role === 'hnl-admin' && HNL_REPORT_EMAILS.includes(email));
  const applyRegionalFilter = useCallback(
    (values = {}) => (role === 'regional_pi' && defaultRegionName
      ? { ...values, regionName: defaultRegionName }
      : values),
    [defaultRegionName, role],
  );

  const [activeReportType, setActiveReportType] = useState(REPORT_TYPES[0].key);
  const [dateMode, setDateMode] = useState('MTD');
  const [regionName, setRegionName] = useState(defaultRegionName);
  const [plantName, setPlantName] = useState('');
  const [productCategory, setProductCategory] = useState('');
  const [productName, setProductName] = useState('');
  const [regionNames, setRegionNames] = useState([]);
  const [plantNames, setPlantNames] = useState([]);
  const [productCategories, setProductCategories] = useState([]);
  const [productNames, setProductNames] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [rows, setRows] = useState([]);
  const [cards, setCards] = useState({});
  const [reportData, setReportData] = useState({});
  const [expandedRegions, setExpandedRegions] = useState({});
  const [expandedPlants, setExpandedPlants] = useState({});

  const toggleRegion = (regionKey) => setExpandedRegions(
    (prev) => ({ ...prev, [regionKey]: !prev[regionKey] }),
  );
  const togglePlant = (plantKey) => setExpandedPlants(
    (prev) => ({ ...prev, [plantKey]: !prev[plantKey] }),
  );

  const getReports = useCallback(
    async (overrides = {}) => {
      if (!canAccessReport) return;
      const queryValues = buildQueryValues({
        reportType: overrides.reportType ?? activeReportType,
        regionName: overrides.regionName ?? regionName,
        plantName: overrides.plantName ?? plantName,
        productCategory: overrides.productCategory ?? productCategory,
        productName: overrides.productName ?? productName,
        dateMode: overrides.dateMode ?? dateMode,
        startDate: overrides.startDate ?? startDate,
        endDate: overrides.endDate ?? endDate,
      });

      try {
        setReportData({});
        setRows([]);
        setExpandedRegions({});
        setExpandedPlants({});
        const response = await dispatch(getIndentSummaryReport(applyRegionalFilter(queryValues)));
        const nextRows = getResponseRows(response);
        setReportData(response || {});
        setRows(nextRows);
        setCards(normaliseInsightCards(response));
      } catch (error) {
        setReportData({});
        setRows([]);
      }
    },
    [
      activeReportType,
      applyRegionalFilter,
      canAccessReport,
      dateMode,
      dispatch,
      endDate,
      plantName,
      productCategory,
      productName,
      regionName,
      startDate,
    ],
  );

  useEffect(() => {
    const fetchData = async () => {
      if (!canAccessReport) return;
      try {
        const response = await dispatch(getFilterDropDownData(defaultRegionName));
        if (response) {
          setRegionNames(defaultRegionName ? [defaultRegionName] : getRows(response.regionNames));
          setPlantNames(getRows(response.plantNames));
          setProductCategories(getRows(response.productCategories));
          setProductNames(getRows(response.productNames));
        }
      } catch (error) {
        setRegionNames(defaultRegionName ? [defaultRegionName] : []);
      }
    };
    fetchData();
  }, [canAccessReport, defaultRegionName, dispatch]);

  useEffect(() => {
    getReports();
  }, [getReports]);

  const clearFilters = () => {
    setRegionName(defaultRegionName);
    setPlantName('');
    setProductCategory('');
    setProductName('');
  };

  const handleDateModeChange = (nextDateMode) => {
    setDateMode(nextDateMode);
    if (nextDateMode === CUSTOM_DATE_MODE && (!startDate || !endDate)) {
      const today = formatInputDate(new Date());
      setStartDate(today);
      setEndDate(today);
    }
  };

  const handleOnFilter = async () => {
    let initialPlantNames = plantNames;
    if (regionName) {
      const response = await dispatch(getFilterDropDownData(regionName));
      initialPlantNames = getRows(response?.plantNames);
    }

    const { value: formValues } = await Swal.fire({
      title: 'Please select filters',
      html: `
        <div class="dropdown-container summary-filter-grid">
          <div class="dropdown-wrapper">
            <label class="summary-filter-label" for="swal-input1">Region Name</label>
            <select id="swal-input1" class="dropdown" ${role === 'regional_pi' ? 'disabled' : ''}>
              <option value="">Select Region Name</option>
              ${getSelectOptions(regionNames, regionName)}
            </select>
          </div>
          <div class="dropdown-wrapper">
            <label class="summary-filter-label" for="swal-input2">Plant Name</label>
            <select id="swal-input2" class="dropdown">
              <option value="">Select Plant Name</option>
              ${getSelectOptions(initialPlantNames, plantName)}
            </select>
          </div>
          <div class="dropdown-wrapper">
            <label class="summary-filter-label" for="swal-input3">Product Category</label>
            <select id="swal-input3" class="dropdown">
              <option value="">Select Product Category</option>
              ${getSelectOptions(productCategories, productCategory)}
            </select>
          </div>
          <div class="dropdown-wrapper">
            <label class="summary-filter-label" for="swal-input4">Product Name</label>
            <select id="swal-input4" class="dropdown">
              <option value="">Select Product Name</option>
              ${getSelectOptions(productNames, productName)}
            </select>
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Apply',
      cancelButtonText: 'Cancel',
      footer: '<button id="clear-filters" class="summary-clear-filters" type="button">Clear Filters</button>',
      focusConfirm: false,
      preConfirm: () => ({
        regionName: defaultRegionName || document.getElementById('swal-input1').value,
        plantName: document.getElementById('swal-input2').value,
        productCategory: document.getElementById('swal-input3').value,
        productName: document.getElementById('swal-input4').value,
      }),
      didOpen: () => {
        document.getElementById('clear-filters').addEventListener('click', () => {
          clearFilters();
          Swal.close();
        });

        if (role !== 'regional_pi') {
          document.getElementById('swal-input1').addEventListener('change', async (event) => {
            const selectedRegion = event.target.value;
            const plantSelect = document.getElementById('swal-input2');
            const response = await dispatch(getFilterDropDownData(selectedRegion));
            const scopedPlantNames = getRows(response?.plantNames);
            plantSelect.innerHTML = `
              <option value="">Select Plant Name</option>
              ${getSelectOptions(scopedPlantNames, '')}
            `;
          });
        }
      },
    });

    if (formValues) {
      setRegionName(formValues.regionName);
      setPlantName(formValues.plantName);
      setProductCategory(formValues.productCategory);
      setProductName(formValues.productName);
    }
  };

  const exportReport = async () => {
    const values = buildQueryValues({
      reportType: activeReportType,
      regionName,
      plantName,
      productCategory,
      productName,
      dateMode,
      startDate,
      endDate,
    });
    await dispatch(getIndentSummaryReportCsv(applyRegionalFilter(values)));
  };

  if (!canAccessReport) {
    return <Redirect to="/admin" />;
  }

  const activeReport = REPORT_TYPES.find(
    (report) => report.key === activeReportType,
  ) || REPORT_TYPES[0];
  const activeDateLabel = DATE_MODES.find((mode) => mode.value === dateMode)?.label || 'Month till date';
  const labels = TABLE_LABELS[activeReport.key] || TABLE_LABELS.orderVsDispatched;
  const plantRowsAll = getPlantRows(reportData, rows);
  const regionRows = getRegionRows(reportData, plantRowsAll);
  const grandTotal = getGrandTotal(reportData, regionRows);

  return (
    <div className="indent-summary-report">
      <div className="summary-report-toolbar">
        <div>
          <h2 className="summary-report-title">Indent Summary Report</h2>
          <p className="summary-report-subtitle">
            {activeReport.label}
            {' - '}
            {dateMode === CUSTOM_DATE_MODE ? `${startDate || '-'} to ${endDate || '-'}` : activeDateLabel}
          </p>
        </div>
        <div className="buttons-container">
          <button type="button" className="button primary filter-button" onClick={handleOnFilter}>
            <FontAwesomeIcon icon={faFilter} className="button__icon" />
            Filter
          </button>
          <button type="button" className="button primary filter-button" onClick={exportReport}>
            <FontAwesomeIcon icon={faFileExcel} className="button__icon" />
            Export
          </button>
        </div>
      </div>

      <div className="summary-card-grid">
        {INSIGHT_CARDS.map((card) => (
          <div className="summary-metric-card" key={card.key}>
            <span className="summary-metric-card__label">{card.label}</span>
            <strong className="summary-metric-card__value">
              {formatNumber(cards?.[card.key])}
            </strong>
            <span className="summary-metric-card__meta">Tonnes</span>
          </div>
        ))}
      </div>

      <div className="date-mode-switches">
        {DATE_MODES.map((mode) => (
          <button
            type="button"
            key={mode.value}
            className={`date-mode-switch ${dateMode === mode.value ? 'active' : ''}`}
            onClick={() => handleDateModeChange(mode.value)}
          >
            {mode.label}
          </button>
        ))}
        {dateMode === CUSTOM_DATE_MODE && (
          <div className="inline-date-range">
            <input
              type="date"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
            />
            <input
              type="date"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
            />
          </div>
        )}
      </div>

      <div className="summary-tabs" role="tablist" aria-label="Indent summary report tabs">
        {REPORT_TYPES.map((report) => (
          <button
            type="button"
            key={report.key}
            className={`summary-tab ${activeReportType === report.key ? 'active' : ''}`}
            onClick={() => setActiveReportType(report.key)}
            role="tab"
            aria-selected={activeReportType === report.key}
          >
            {report.label}
          </button>
        ))}
      </div>

      <div className="temporary-certifcates">
        <div className="table-container summary-combined-table summary-drilldown-container">
          <table className="table table-bordered summary-report-table summary-drilldown-table">
            <thead>
              <tr>
                <th rowSpan={2} className="summary-drilldown-name-col">Region</th>
                {CATEGORY_TABLES.map((category) => (
                  <th colSpan={3} key={category.key}>{category.title}</th>
                ))}
              </tr>
              <tr>
                {CATEGORY_TABLES.map((category) => (
                  <React.Fragment key={`${category.key}-headers`}>
                    <th>{labels.primary}</th>
                    <th>{labels.secondary}</th>
                    <th>{labels.percent}</th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {regionRows.length === 0 && (
                <tr>
                  <td className="summary-empty-state" colSpan={10}>
                    No records found.
                  </td>
                </tr>
              )}
              {regionRows.map((region) => {
                const isRegionExpanded = !!expandedRegions[region.regionName];
                const plantRowsForRegion = plantRowsAll.filter(
                  (row) => row.regionName === region.regionName,
                );
                const plantSummaryRows = isRegionExpanded
                  ? getPlantSummaryRows(plantRowsForRegion)
                  : [];

                return (
                  <React.Fragment key={region.regionName}>
                    <tr className="summary-region-row">
                      <td className="summary-drilldown-name-col">
                        <button
                          type="button"
                          className="summary-expand-btn"
                          onClick={() => toggleRegion(region.regionName)}
                        >
                          {isRegionExpanded ? '-' : '+'}
                        </button>
                        {region.regionName}
                      </td>
                      {CATEGORY_TABLES.map((category) => (
                        <React.Fragment key={category.key}>
                          <td>{formatCategoryQuantity(region[`${category.prefix}Qty`], category)}</td>
                          <td>
                            {formatCategoryQuantity(region[`${category.prefix}FullFillQty`], category)}
                          </td>
                          <td>{formatNumber(region[`${category.prefix}FullFillPer`])}</td>
                        </React.Fragment>
                      ))}
                    </tr>

                    {isRegionExpanded && plantSummaryRows.map((plant) => {
                      const plantKey = `${region.regionName}|${plant.plantName}`;
                      const isPlantExpanded = !!expandedPlants[plantKey];
                      const materialRowsForPlant = isPlantExpanded
                        ? plantRowsForRegion.filter((row) => row.plantName === plant.plantName)
                        : [];
                      const materialCategoryData = isPlantExpanded
                        ? getMaterialCategoryData(materialRowsForPlant)
                        : [];

                      return (
                        <React.Fragment key={plantKey}>
                          <tr className="summary-plant-row">
                            <td className="summary-drilldown-name-col summary-plant-name-cell">
                              <button
                                type="button"
                                className="summary-expand-btn"
                                onClick={() => togglePlant(plantKey)}
                              >
                                {isPlantExpanded ? '-' : '+'}
                              </button>
                              {plant.plantName}
                            </td>
                            {CATEGORY_TABLES.map((category) => (
                              <React.Fragment key={category.key}>
                                <td>
                                  {formatCategoryQuantity(plant[`${category.prefix}Qty`], category)}
                                </td>
                                <td>
                                  {formatCategoryQuantity(plant[`${category.prefix}FullFillQty`], category)}
                                </td>
                                <td>{formatNumber(plant[`${category.prefix}FullFillPer`])}</td>
                              </React.Fragment>
                            ))}
                          </tr>

                          {isPlantExpanded && (
                            <tr className="summary-material-drilldown-row">
                              <td colSpan={10}>
                                <MaterialDrilldownTable
                                  materialCategoryData={materialCategoryData}
                                  labels={labels}
                                />
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                );
              })}
              {regionRows.length > 0 && (
                <tr className="summary-total-row">
                  <td>Total</td>
                  {CATEGORY_TABLES.map((category) => (
                    <React.Fragment key={category.key}>
                      <td>
                        {formatCategoryQuantity(grandTotal[category.key].orderedQty, category)}
                      </td>
                      <td>
                        {formatCategoryQuantity(grandTotal[category.key].fullFilledQty, category)}
                      </td>
                      <td>{formatNumber(grandTotal[category.key].fulfillmentPercentage)}</td>
                    </React.Fragment>
                  ))}
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default HnlIndentingSummaryReport;
